import type { FC } from "react"

import { ChevronDownIcon } from "@heroicons/react/24/outline"

export type GreenbuttonDrawerTabId =
  | `overview`
  | `solarFit`
  | `support`
  | `typicalDay`
  | `usagePatterns`

export type GreenbuttonDrawerTab = {
  id: GreenbuttonDrawerTabId
  label: string
  /** When true, mobile select suffixes the label with "(needs more data)". */
  needsMoreData?: boolean
}

export const GREENBUTTON_DRAWER_TABS: readonly GreenbuttonDrawerTab[] = [
  { id: `overview`, label: `Overview` },
  { id: `typicalDay`, label: `Typical day` },
  { id: `usagePatterns`, label: `Usage patterns` },
  { id: `solarFit`, label: `Solar fit` },
] as const

/**
 * Appended at the end of the drawer only for admins who can see sanitized
 * Green Button support diagnostics and CSV recovery — sellers never see it.
 */
export const GREENBUTTON_SUPPORT_TAB: GreenbuttonDrawerTab = {
  id: `support`,
  label: `Support`,
}

type GreenbuttonDrawerTabBarProps = {
  activeTab: GreenbuttonDrawerTabId
  onTabChange: (tab: GreenbuttonDrawerTabId) => void
  tabs?: readonly GreenbuttonDrawerTab[]
}

const mobileTabLabel = (tab: GreenbuttonDrawerTab): string =>
  tab.needsMoreData ? `${tab.label} (needs more data)` : tab.label

export const GreenbuttonDrawerTabBar: FC<GreenbuttonDrawerTabBarProps> = ({
  activeTab,
  onTabChange,
  tabs = GREENBUTTON_DRAWER_TABS,
}) => (
  <>
    {/* Mobile: Tailwind UI "tabs with select" — native select + chevron. */}
    <div className="border-b border-border bg-background px-6 py-3 sm:hidden">
      <div className="grid grid-cols-1">
        <select
          aria-label="Usage view"
          value={activeTab}
          className="col-start-1 row-start-1 h-11 w-full appearance-none rounded-lg border border-border bg-background pr-9 pl-3 font-display text-base font-semibold text-foreground focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus-ring"
          onChange={(event) =>
            onTabChange(event.target.value as GreenbuttonDrawerTabId)
          }
        >
          {tabs.map((tab) => (
            <option key={tab.id} value={tab.id}>
              {mobileTabLabel(tab)}
            </option>
          ))}
        </select>
        <ChevronDownIcon
          aria-hidden="true"
          className="pointer-events-none col-start-1 row-start-1 mr-3 size-5 self-center justify-self-end text-muted"
        />
      </div>
    </div>
    <nav
      aria-label="Usage views"
      className="hidden gap-1 overflow-x-auto border-b border-border bg-background px-6 sm:flex"
    >
      {tabs.map((tab) => {
        const isActive = tab.id === activeTab
        return (
          <button
            key={tab.id}
            type="button"
            aria-current={isActive ? `page` : undefined}
            className={[
              `relative shrink-0 px-4 py-3 font-display text-sm font-semibold transition-colors focus-visible:outline-2 focus-visible:outline-focus-ring`,
              isActive ? `text-primary` : `text-muted hover:text-foreground`,
            ].join(` `)}
            onClick={() => onTabChange(tab.id)}
          >
            {tab.label}
            {isActive ? (
              <span className="absolute inset-x-3 bottom-0 h-0.5 rounded-full bg-primary" />
            ) : null}
          </button>
        )
      })}
    </nav>
  </>
)
